import { api } from "./apiClient";

/**
 * Marketplace API - Venue recommendations, search, and check-in verification
 */

/**
 * Get venue recommendations based on activity and location
 * @param {Object} params - Query parameters
 * @param {string} params.activity - Activity type (e.g., "coffee", "study", "dinner")
 * @param {number} params.user_lat - User's latitude
 * @param {number} params.user_lng - User's longitude
 * @param {number} [params.limit=5] - Maximum number of venues to return
 * @returns {Promise<{venues: Array, activity: string, location: Object, count: number}>}
 */
export const getVenueRecommendations = async ({
  activity,
  user_lat,
  user_lng,
  limit = 5,
}) => {
  const response = await api.get("/api/marketplace/venues/recommend", {
    params: { activity, user_lat, user_lng, limit },
  });
  return response.data;
};

/**
 * Search venues by query, city, or type
 * @param {Object} params - Search parameters
 * @param {string} [params.query] - Search query (name/address)
 * @param {string} [params.city] - City name
 * @param {string} [params.venue_type] - Venue type (cafe, restaurant, gym, etc.)
 * @param {number} [params.user_lat] - User's latitude for distance calculation
 * @param {number} [params.user_lng] - User's longitude
 * @param {number} [params.limit=10] - Maximum results
 * @returns {Promise<{venues: Array, count: number}>}
 */
export const searchVenues = async ({
  query,
  city,
  venue_type,
  user_lat,
  user_lng,
  limit = 10,
}) => {
  const response = await api.get("/api/marketplace/venues/search", {
    params: { query, city, venue_type, user_lat, user_lng, limit },
  });
  return response.data;
};

/**
 * Get venue details by ID
 * @param {string} venueId - Venue UUID
 * @returns {Promise<{venue: Object}>}
 */
export const getVenueById = async (venueId) => {
  const response = await api.get(`/api/marketplace/venues/${venueId}`);
  return response.data;
};

/**
 * Get venue perks/offers
 * @param {string} venueId - Venue UUID
 * @returns {Promise<{offer: string}>}
 */
export const getVenuePerks = async (venueId) => {
  const response = await api.get("/api/marketplace/venues/perks", {
    params: { venue_id: venueId },
  });
  return response.data;
};

/**
 * Get activity-to-venue type mappings for client hints
 * @returns {Promise<{mappings: Array<{activity_keyword: string, venue_types: string[], priority: number}>}>}
 */
export const getActivityMappings = async () => {
  const response = await api.get("/api/marketplace/activity-mappings");
  return response.data;
};

/**
 * Get featured partner venues with active offers
 * Used for the "Hot Deals" section in the create linkup flow
 * @param {Object} params - Query parameters
 * @param {number} params.user_lat - User's latitude
 * @param {number} params.user_lng - User's longitude
 * @param {number} [params.limit=4] - Maximum number of venues to return
 * @returns {Promise<{venues: Array, count: number, location: Object}>}
 */
export const getFeaturedVenues = async ({ user_lat, user_lng, limit = 4 }) => {
  const response = await api.get("/api/marketplace/venues/featured", {
    params: { user_lat, user_lng, limit },
  });
  return response.data;
};

/**
 * Verify check-in at a venue
 * @param {Object} params - Check-in data
 * @param {string} params.linkup_id - Linkup UUID
 * @param {string} params.linkup_request_id - Linkup Request UUID (for tracking who checked in together)
 * @param {string} params.user_id - User UUID who is checking in
 * @param {number} params.user_lat - User's current latitude
 * @param {number} params.user_lng - User's current longitude
 * @returns {Promise<{success: boolean, verified: boolean, venue: Object, distance_meters: number, check_in: Object, both_checked_in: boolean}>}
 */
export const verifyCheckIn = async ({
  linkup_id,
  linkup_request_id,
  user_id,
  user_lat,
  user_lng,
}) => {
  const response = await api.post("/api/marketplace/verify/check-in", {
    linkup_id,
    linkup_request_id,
    user_id,
    user_lat,
    user_lng,
  });
  return response.data;
};

/**
 * Get check-in status for a linkup request
 * @param {string} linkup_request_id - The linkup request UUID
 * @param {string} [user_id] - Current user's UUID (to determine my_check_in status)
 * @returns {Promise<{success: boolean, check_ins: Array, total_checked_in: number, both_checked_in: boolean, my_check_in: boolean, partner_check_in: boolean, attribution: Object|null}>}
 */
export const getCheckInStatus = async (linkup_request_id, user_id) => {
  const response = await api.get(
    `/api/linkup/linkups/check-in-status/${linkup_request_id}`,
    { params: user_id ? { user_id } : {} },
  );
  return response.data;
};

/**
 * Track venue click for analytics (fire-and-forget)
 * @param {Object} params - Tracking data
 * @param {string} params.venue_id - Venue UUID
 * @param {string} params.source - Click source (e.g., "widget_featured", "feed_featured")
 * @param {string} [params.user_city] - User's city
 * @param {string} [params.activity] - Activity context
 * @returns {Promise<void>}
 */
export const trackVenueClick = async ({
  venue_id,
  source,
  user_city,
  activity,
}) => {
  try {
    await api.post("/api/marketplace/analytics/track-click", {
      venue_id,
      source,
      user_city,
      activity,
    });
  } catch (e) {
    // Silently fail - analytics should never break the UX
    console.debug("[trackVenueClick] Failed:", e.message);
  }
};

export default {
  getVenueRecommendations,
  searchVenues,
  getVenueById,
  getVenuePerks,
  getActivityMappings,
  getFeaturedVenues,
  verifyCheckIn,
  getCheckInStatus,
  trackVenueClick,
};
